/** One week's box, opened. Public — no sign-in required. */

import { useEffect, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';
import type { JSX } from 'preact';

import { apiGet } from '../api.js';

interface BoxWeekDetail {
  id: string;
  season_year: number;
  week_number: number;
  headline: string;
  body: string | null;
  media: Array<{ url: string | null; content_type: string; alt_text: string | null }>;
  items: Array<{ label: string; quantity_per_box: number | null; unit: string | null; blurb: string | null }>;
}

export function BoxWeekView({ id }: { id: string }): JSX.Element {
  const [week, setWeek] = useState<BoxWeekDetail | 'not_found' | null>(null);

  useEffect(() => {
    apiGet<BoxWeekDetail>(`/public/v1/box-weeks/${id}`)
      .then(setWeek)
      .catch(() => setWeek('not_found'));
  }, [id]);

  if (week === null) {
    return (
      <main class="view centred">
        <p class="muted" role="status">Opening…</p>
      </main>
    );
  }

  if (week === 'not_found') {
    return (
      <main class="view centred">
        <h1>Not found</h1>
        <p class="muted">That week isn't posted, or isn't public yet.</p>
        <Link href="/box" class="primary">
          Back to the box journal
        </Link>
      </main>
    );
  }

  return (
    <main class="view reveal">
      <Link href="/box" class="link">
        ← All weeks
      </Link>

      <header>
        <span class="pack-card-week">
          {week.season_year} · Week {week.week_number}
        </span>
        <h1>{week.headline}</h1>
      </header>

      <div class="box-layout">
      <div class="box-story">
      {week.body && <p>{week.body}</p>}
      {week.media.map((asset, index) =>
        asset.url && asset.content_type.startsWith('video/') ? (
          <div class="reveal-media" key={index}>
            <video src={asset.url} controls playsInline />
          </div>
        ) : asset.url ? (
          <div class="reveal-media" key={index}>
            <img src={asset.url} alt={asset.alt_text ?? ''} loading="lazy" />
          </div>
        ) : null,
      )}
      </div>
      {week.items.length > 0 && <section class="box-contents"><span class="ff-eyebrow">This week’s harvest</span><h2>In your box</h2>
      {week.items.map((item, index) => (
        <div class="card" key={index}>
          <span class="card-title">
            {item.label}
            {item.quantity_per_box != null && item.unit ? ` · ${item.quantity_per_box} ${item.unit}/box` : ''}
          </span>
          {item.blurb && <p class="card-sub">{item.blurb}</p>}
        </div>
      ))}

      </section>}
      </div>
    </main>
  );
}
